/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * SYSTEM MODULES - Single Source of Truth
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * This file defines all module codes used across the platform.
 * When adding a new module:
 * 1. Add the code to MODULE_CODES constant
 * 2. Add module details to MODULES array
 * 3. Update plan definitions in seed.js to include the new module
 *
 * NO CODE CHANGES needed in routes or components - they import from here.
 *
 * Last Updated: February 2026
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import type {
  ModuleCategory,
  ModuleDefinition,
  SidebarConfig,
  SidebarItem,
} from "../types/module.types";

// Re-export types for convenience
export type { ModuleCategory, ModuleDefinition, SidebarConfig, SidebarItem };

/**
 * Module code constants - Use these throughout the codebase
 * instead of hardcoded strings.
 *
 * Sidebar Groups & Sorting Order:
 * 1. Overview (Dashboard)
 * 2. POS
 * 3. Sales (Billing Sessions, Counters, Invoices, Sales Returns, Discount)
 * 4. Procurement (Goods Receipts, Purchase Orders, Purchase Returns, Supplier Bills, Suppliers, Manufacturers)
 * 5. Inventory (Products, Stock View, Stock Adjustments, Stock Counts, Rack Locations, Drug Generics, Batch)
 * 6. Reports
 * 7. Customer
 * 8. Healthcare (Hospitals, Doctors)
 * 9. Programs (Loyalty)
 * 10. Users (Users, Permissions)
 * 11. Stores
 * 12. Compliance (Schedule H1)
 * 13. Drug License
 * 14. Settings (Profile, Settings, Subscription)
 */
export const MODULE_CODES = {
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE - Essential features included in all plans
  // ═══════════════════════════════════════════════════════════════════════════
  DASHBOARD: "DASHBOARD", // Sidebar Group: Overview, Order: 1
  WEB_APP: "WEB_APP", // Platform access - Web Application
  SALES_MGMT: "SALES_MGMT", // Sidebar Group: POS (2), Sales (3)
  INVENTORY: "INVENTORY", // Sidebar Group: Inventory, Order: 5
  CUSTOMER_MGMT: "CUSTOMER_MGMT", // Sidebar Group: Customer, Order: 7
  REPORTS: "REPORTS", // Sidebar Group: Reports, Order: 6
  USER_MGMT: "USER_MGMT", // Sidebar Group: Users, Order: 10
  SETTINGS: "SETTINGS", // Sidebar Group: Settings, Order: 14
  ACCOUNT_BILLING: "ACCOUNT_BILLING", // Account-level subscription and billing

  // ═══════════════════════════════════════════════════════════════════════════
  // ADVANCED - Growth features
  // ═══════════════════════════════════════════════════════════════════════════
  PROCUREMENT: "PROCUREMENT", // Sidebar Group: Procurement, Order: 4
  SUPPLIER_MGMT: "SUPPLIER_MGMT", // Sidebar Group: Procurement, Order: 4
  MULTI_STORE: "MULTI_STORE", // Sidebar Group: Inventory (5), Stores (11)
  LOYALTY_PROGRAM: "LOYALTY_PROGRAM", // Sidebar Group: Programs, Order: 9
  DISCOUNT: "DISCOUNT", // Sidebar Group: Sales, Order: 3
  DOCTOR_MGMT: "DOCTOR_MGMT", // Sidebar Group: Healthcare, Order: 8
  HOSPITAL_MGMT: "HOSPITAL_MGMT", // Sidebar Group: Healthcare, Order: 8
  DRUG_GENERICS: "DRUG_GENERICS", // Sidebar Group: Inventory, Order: 5

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPLIANCE - Pharmacy regulatory features
  // ═══════════════════════════════════════════════════════════════════════════
  COMPLIANCE: "COMPLIANCE", // Sidebar Group: Compliance, Order: 12
  DRUG_LICENSE: "DRUG_LICENSE", // Sidebar Group: Drug License, Order: 13

  // ═══════════════════════════════════════════════════════════════════════════
  // PREMIUM - Enterprise features
  // ═══════════════════════════════════════════════════════════════════════════
  DESKTOP_APP: "DESKTOP_APP",
  MOBILE_APP: "MOBILE_APP",
  OFFLINE_MODE: "OFFLINE_MODE",
  API_ACCESS: "API_ACCESS",
  WHITE_LABEL: "WHITE_LABEL",
  PRIORITY_SUPPORT: "PRIORITY_SUPPORT",
} as const;

/**
 * Type for module codes - use this for type safety
 */
export type ModuleCode = (typeof MODULE_CODES)[keyof typeof MODULE_CODES];

/**
 * Array of all module codes - useful for validation
 */
export const ALL_MODULE_CODES: ModuleCode[] = Object.values(MODULE_CODES);

/**
 * Sidebar Group configuration
 */
export interface SidebarGroupConfig {
  title: string;
  sortOrder: number;
}

export const SIDEBAR_GROUPS: Record<string, SidebarGroupConfig> = {
  Overview: { title: "Overview", sortOrder: 1 },
  Sales: { title: "Sales", sortOrder: 2 },
  Procurement: { title: "Procurement", sortOrder: 3 },
  Inventory: { title: "Inventory", sortOrder: 4 },
  Reports: { title: "Reports", sortOrder: 5 },
  Customers: { title: "Customers", sortOrder: 6 },
  Healthcare: { title: "Healthcare", sortOrder: 7 },
  Programs: { title: "Programs", sortOrder: 8 },
  Users: { title: "Users", sortOrder: 9 },
  Stores: { title: "Stores", sortOrder: 10 },
  Compliance: { title: "Compliance", sortOrder: 11 },
  Licensing: { title: "Licensing", sortOrder: 12 },
  Settings: { title: "Settings", sortOrder: 13 },
};

/**
 * Module config entry for constants (seed data structure)
 */
export interface ModuleConfig {
  code: ModuleCode;
  name: string;
  category: ModuleCategory;
  description: string;
  sortOrder: number;
  icon: string;
  routes: string[];
  sidebarConfig?: SidebarConfig;
}

/**
 * Complete module definitions with all metadata
 * This is the authoritative source for module information.
 */
export const MODULES: ModuleConfig[] = [
  // ═══════════════════════════════════════════════════════════════════════════
  // CORE - Essential features included in all plans
  // ═══════════════════════════════════════════════════════════════════════════
  {
    code: MODULE_CODES.DASHBOARD,
    name: "Dashboard",
    category: "CORE",
    description: "Overview and analytics dashboard",
    sortOrder: 1,
    icon: "LayoutDashboard",
    routes: ["/dashboard"],
    sidebarConfig: {
      title: "Overview",
      items: [
        { name: "Dashboard", href: "/dashboard", icon: "LayoutDashboard" },
      ],
    },
  },
  {
    code: MODULE_CODES.WEB_APP,
    name: "Web Application",
    category: "CORE",
    description: "Access to the web application",
    sortOrder: 1,
    icon: "Globe",
    routes: [],
  },
  {
    code: MODULE_CODES.SALES_MGMT,
    name: "Sales Management",
    category: "CORE",
    description:
      "POS billing, invoices, sales returns, counters, and billing sessions",
    sortOrder: 2,
    icon: "ShoppingCart",
    routes: [
      "/pos",
      "/invoices",
      "/invoices/:id",
      "/sales-returns",
      "/sales-returns/create",
      "/sales-returns/:id",
      "/approvals",
      "/billing-sessions",
      "/billing-sessions/open",
      "/billing-sessions/:id",
      "/billing-sessions/:id/close",
      "/counters",
      "/counters/create",
      "/counters/:id/edit",
    ],
    sidebarConfig: {
      title: "Sales",
      items: [
        { name: "POS", href: "/pos", icon: "ShoppingCart" },
        {
          name: "Billing Sessions",
          href: "/billing-sessions",
          icon: "DollarSign",
        },
        { name: "Counters", href: "/counters", icon: "LayoutGrid" },
        { name: "Invoices", href: "/invoices", icon: "FileText" },
        {
          name: "Sales Returns",
          href: "/sales-returns",
          icon: "ArrowRightLeft",
        },
        {
          name: "Approval Center",
          href: "/approvals",
          icon: "ClipboardCheck",
        },
        { name: "Discounts", href: "/discount-schemes", icon: "Percent" },
      ],
    },
  },
  {
    code: MODULE_CODES.INVENTORY,
    name: "Inventory",
    category: "CORE",
    description:
      "Products, stock view, stock adjustments, physical stock counts, rack locations, categories, and batch tracking",
    sortOrder: 5,
    icon: "Boxes",
    routes: [
      "/products",
      "/products/new",
      "/products/:id",
      "/products/:id/edit",
      "/products/import",
      "/inventory",
      "/stock-adjustments",
      "/physical-stock-counts",
      "/physical-stock-counts/create",
      "/physical-stock-counts/:id",
      "/categories",
      "/rack-locations",
      "/rack-locations/create",
      "/rack-locations/:id/edit",
      "/batches",
    ],
    sidebarConfig: {
      title: "Inventory",
      items: [
        { name: "Products", href: "/products", icon: "Pill" },
        { name: "Batches", href: "/batches", icon: "ClipboardList" },
        { name: "Stock View", href: "/inventory", icon: "Boxes" },
        {
          name: "Stock Adjustments",
          href: "/stock-adjustments",
          icon: "SlidersHorizontal",
        },
        {
          name: "Stock Counts",
          href: "/physical-stock-counts",
          icon: "ClipboardCheck",
        },
        { name: "Rack Locations", href: "/rack-locations", icon: "MapPin" },
        { name: "Drug Generics", href: "/drug-generics", icon: "FlaskConical" },
      ],
    },
  },
  {
    code: MODULE_CODES.CUSTOMER_MGMT,
    name: "Customer Management",
    category: "CORE",
    description: "Manage customer profiles, history, and ledger",
    sortOrder: 7,
    icon: "Users",
    routes: [
      "/customers",
      "/customers/create",
      "/customers/:id/edit",
      "/customers/:id/ledger",
    ],
    sidebarConfig: {
      title: "Customers",
      items: [{ name: "All Customers", href: "/customers", icon: "Users" }],
    },
  },
  {
    code: MODULE_CODES.REPORTS,
    name: "Reports",
    category: "CORE",
    description: "Sales, stock, expiry, GST, and other reports",
    sortOrder: 6,
    icon: "BarChart3",
    routes: [
      "/reports",
      "/reports/sales",
      "/reports/stock",
      "/reports/expiry",
      "/reports/purchases",
      "/reports/day-book",
      "/reports/cash-book",
      "/reports/counter",
      "/reports/end-of-day",
      "/reports/supplier-outstanding",
      "/reports/outstanding",
      "/reports/gst",
    ],
    sidebarConfig: {
      title: "Reports",
      items: [
        { name: "Reports Dashboard", href: "/reports", icon: "BarChart3" },
      ],
    },
  },
  {
    code: MODULE_CODES.USER_MGMT,
    name: "Users",
    category: "CORE",
    description: "Manage users, roles, and permissions",
    sortOrder: 10,
    icon: "Users",
    routes: ["/users", "/users/create", "/users/:id/edit", "/permissions"],
    sidebarConfig: {
      title: "Users",
      items: [
        { name: "Users", href: "/users", icon: "Users" },
        { name: "Permissions", href: "/permissions", icon: "Key" },
      ],
    },
  },
  {
    code: MODULE_CODES.SETTINGS,
    name: "Settings",
    category: "CORE",
    description: "Profile and pharmacy settings",
    sortOrder: 14,
    icon: "Settings",
    routes: ["/settings", "/profile"],
    sidebarConfig: {
      title: "Settings",
      items: [
        { name: "Profile", href: "/profile", icon: "User" },
        { name: "Settings", href: "/settings", icon: "Settings" },
      ],
    },
  },
  {
    code: MODULE_CODES.ACCOUNT_BILLING,
    name: "Account Billing",
    category: "CORE",
    description: "Subscription, renewal, and account billing management",
    sortOrder: 15,
    icon: "CreditCard",
    routes: [
      "/subscription",
      "/subscription/overview",
      "/subscription/success",
      "/subscription/failure",
      "/subscription/history",
    ],
    sidebarConfig: {
      title: "Settings",
      items: [
        { name: "Subscription", href: "/subscription", icon: "CreditCard" },
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // ADVANCED - Growth features
  // ═══════════════════════════════════════════════════════════════════════════
  {
    code: MODULE_CODES.PROCUREMENT,
    name: "Procurement",
    category: "ADVANCED",
    description:
      "Goods receipts, purchase orders, purchase returns, and supplier bills",
    sortOrder: 4,
    icon: "PackageCheck",
    routes: [
      "/goods-receipts",
      "/goods-receipts/new",
      "/goods-receipts/:id",
      "/purchase-orders",
      "/purchase-orders/new",
      "/purchase-orders/create",
      "/purchase-orders/:id",
      "/purchase-orders/:id/edit",
      "/purchase-returns",
      "/purchase-returns/create",
      "/purchase-returns/:id",
      "/supplier-bills",
      "/supplier-bills/:id",
    ],
    sidebarConfig: {
      title: "Procurement",
      items: [
        {
          name: "Goods Receipts",
          href: "/goods-receipts",
          icon: "PackageCheck",
        },
        {
          name: "Purchase Orders",
          href: "/purchase-orders",
          icon: "ShoppingBag",
        },
        {
          name: "Purchase Returns",
          href: "/purchase-returns",
          icon: "ArrowRightLeft",
        },
        { name: "Supplier Bills", href: "/supplier-bills", icon: "FileText" },
        { name: "Suppliers", href: "/suppliers", icon: "Truck" },
        { name: "Manufacturers", href: "/manufacturers", icon: "Factory" },
      ],
    },
  },
  {
    code: MODULE_CODES.SUPPLIER_MGMT,
    name: "Supplier",
    category: "ADVANCED",
    description: "Manage suppliers and manufacturers",
    sortOrder: 4,
    icon: "Truck",
    routes: [
      "/suppliers",
      "/suppliers/create",
      "/suppliers/:id",
      "/suppliers/:id/edit",
      "/manufacturers",
      "/manufacturers/create",
      "/manufacturers/:id",
      "/manufacturers/:id/edit",
    ],
    // Sidebar items merged into PROCUREMENT group
  },
  {
    code: MODULE_CODES.MULTI_STORE,
    name: "Multi-store",
    category: "ADVANCED",
    description: "Manage multiple store locations and stock transfers",
    sortOrder: 11,
    icon: "Store",
    routes: [
      "/stores",
      "/stores/create",
      "/stores/:id/edit",
      "/stock-transfers",
      "/stock-transfers/create",
    ],
    sidebarConfig: {
      title: "Stores",
      items: [
        { name: "Stock Transfers", href: "/stock-transfers", icon: "Repeat" },
        { name: "Stores", href: "/stores", icon: "Store" },
      ],
    },
  },
  {
    code: MODULE_CODES.LOYALTY_PROGRAM,
    name: "Loyalty Programs",
    category: "ADVANCED",
    description: "Customer loyalty points and rewards",
    sortOrder: 9,
    icon: "Award",
    routes: [
      "/loyalty-programs",
      "/loyalty-programs/create",
      "/loyalty-programs/:id/edit",
    ],
    sidebarConfig: {
      title: "Programs",
      items: [
        { name: "Loyalty Program", href: "/loyalty-programs", icon: "Award" },
      ],
    },
  },
  {
    code: MODULE_CODES.DISCOUNT,
    name: "Discount",
    category: "ADVANCED",
    description: "Configure discount schemes and promotional pricing",
    sortOrder: 3,
    icon: "Percent",
    routes: [
      "/discount-schemes",
      "/discount-schemes/create",
      "/discount-schemes/:id/edit",
    ],
    // Sidebar items merged into Sales group (SALES_MGMT)
  },
  {
    code: MODULE_CODES.DOCTOR_MGMT,
    name: "Doctor Management",
    category: "ADVANCED",
    description: "Manage doctors for prescriptions",
    sortOrder: 8,
    icon: "Stethoscope",
    routes: [
      "/doctors",
      "/doctors/create",
      "/doctors/:id",
      "/doctors/:id/edit",
    ],
    sidebarConfig: {
      title: "Healthcare",
      items: [
        { name: "Doctors", href: "/doctors", icon: "Stethoscope" },
        { name: "Hospitals", href: "/hospitals", icon: "Building2" },
      ],
    },
  },
  {
    code: MODULE_CODES.HOSPITAL_MGMT,
    name: "Hospital Management",
    category: "ADVANCED",
    description: "Manage hospitals for prescriptions",
    sortOrder: 8,
    icon: "Building2",
    routes: [
      "/hospitals",
      "/hospitals/create",
      "/hospitals/:id",
      "/hospitals/:id/edit",
    ],
    // Sidebar items merged into Healthcare group (DOCTOR_MGMT)
  },
  {
    code: MODULE_CODES.DRUG_GENERICS,
    name: "Drug Generics",
    category: "ADVANCED",
    description: "Manage drug generic names and compositions",
    sortOrder: 5,
    icon: "FlaskConical",
    routes: [
      "/drug-generics",
      "/drug-generics/create",
      "/drug-generics/:id/edit",
    ],
    // Sidebar items merged into Inventory group (INVENTORY)
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // COMPLIANCE - Pharmacy regulatory features
  // ═══════════════════════════════════════════════════════════════════════════
  {
    code: MODULE_CODES.COMPLIANCE,
    name: "Compliance",
    category: "COMPLIANCE",
    description: "Schedule H1 register and GST/HSN compliance",
    sortOrder: 12,
    icon: "Shield",
    routes: [
      "/compliance/schedule-h1",
      "/hsn-codes",
      "/hsn-codes/create",
      "/hsn-codes/:id/edit",
    ],
    sidebarConfig: {
      title: "Compliance",
      items: [
        {
          name: "Schedule H1",
          href: "/compliance/schedule-h1",
          icon: "FileWarning",
        },
        { name: "GST Compliance", href: "/hsn-codes", icon: "Hash" },
      ],
    },
  },
  {
    code: MODULE_CODES.DRUG_LICENSE,
    name: "Drug License",
    category: "COMPLIANCE",
    description: "Manage drug licenses and regulatory documents",
    sortOrder: 13,
    icon: "BadgeCheck",
    routes: [
      "/drug-licenses",
      "/drug-licenses/create",
      "/drug-licenses/:id/edit",
    ],
    sidebarConfig: {
      title: "Licensing",
      items: [
        { name: "Drug Licenses", href: "/drug-licenses", icon: "BadgeCheck" },
      ],
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // PREMIUM - Enterprise features
  // ═══════════════════════════════════════════════════════════════════════════
  {
    code: MODULE_CODES.DESKTOP_APP,
    name: "Desktop Application",
    category: "PREMIUM",
    description: "Native desktop app with offline support",
    sortOrder: 2,
    icon: "Monitor",
    routes: [],
  },
  {
    code: MODULE_CODES.MOBILE_APP,
    name: "Mobile Application",
    category: "PREMIUM",
    description: "Mobile app for on-the-go management",
    sortOrder: 3,
    icon: "Smartphone",
    routes: [],
  },
  {
    code: MODULE_CODES.OFFLINE_MODE,
    name: "Offline Mode",
    category: "PREMIUM",
    description: "Work without internet, auto-sync when connected",
    sortOrder: 30,
    icon: "WifiOff",
    routes: [],
  },
  {
    code: MODULE_CODES.API_ACCESS,
    name: "API Integration",
    category: "PREMIUM",
    description: "REST API access for custom integrations",
    sortOrder: 40,
    icon: "Code",
    routes: [],
  },
  {
    code: MODULE_CODES.WHITE_LABEL,
    name: "White Labeling",
    category: "PREMIUM",
    description: "Custom branding and domain",
    sortOrder: 41,
    icon: "Palette",
    routes: [],
  },
  {
    code: MODULE_CODES.PRIORITY_SUPPORT,
    name: "Priority Support",
    category: "PREMIUM",
    description: "24/7 dedicated support channel",
    sortOrder: 42,
    icon: "Headphones",
    routes: [],
  },
];

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get module definition by code
 */
export function getModuleByCode(code: ModuleCode): ModuleConfig | undefined {
  return MODULES.find((m) => m.code === code);
}

/**
 * Get all modules by category
 */
export function getModulesByCategory(category: ModuleCategory): ModuleConfig[] {
  return MODULES.filter((m) => m.category === category);
}

/**
 * Get module code for a route path
 * Returns the module code that protects this route, or undefined if not protected
 */
export function getModuleForRoute(routePath: string): ModuleCode | undefined {
  // Normalize route (remove trailing slash, handle params)
  const normalizedRoute = routePath.replace(/\/$/, "");

  for (const module of MODULES) {
    for (const route of module.routes) {
      // Convert route pattern to regex (handle :param patterns)
      const routePattern = route
        .replace(/:[^/]+/g, "[^/]+")
        .replace(/\//g, "\\/");
      const regex = new RegExp(`^${routePattern}$`);

      if (regex.test(normalizedRoute)) {
        return module.code;
      }
    }
  }

  return undefined;
}

/**
 * Check if a module code is valid
 */
export function isValidModuleCode(code: string): code is ModuleCode {
  return ALL_MODULE_CODES.includes(code as ModuleCode);
}

/**
 * Temporary one-to-one aliases used while legacy plan and controller data is
 * migrated. Product bundles such as CORE_PHARMACY and GROWTH intentionally do
 * not appear here because they expand to more than one canonical module.
 */
export const MODULE_CODE_ALIASES: Readonly<Record<string, ModuleCode>> = {
  POS_BILLING: MODULE_CODES.SALES_MGMT,
  PAYMENTS: MODULE_CODES.SALES_MGMT,
  REPORTS_BASIC: MODULE_CODES.REPORTS,
  REPORTS_ADVANCED: MODULE_CODES.REPORTS,
};

export function canonicalModuleCode(code: string): string {
  const normalized = code.trim().toUpperCase();
  return MODULE_CODE_ALIASES[normalized] ?? normalized;
}

/**
 * Get all routes protected by a module
 */
export function getRoutesForModule(code: ModuleCode): string[] {
  const module = getModuleByCode(code);
  return module?.routes ?? [];
}

// ═══════════════════════════════════════════════════════════════════════════════
// PLAN DEFINITIONS - Which modules are included in each plan
// ═══════════════════════════════════════════════════════════════════════════════

export const PLAN_MODULES = {
  STARTER: [
    MODULE_CODES.WEB_APP,
    MODULE_CODES.DASHBOARD,
    MODULE_CODES.SALES_MGMT,
    MODULE_CODES.INVENTORY,
    MODULE_CODES.CUSTOMER_MGMT,
    MODULE_CODES.REPORTS,
    MODULE_CODES.USER_MGMT,
    MODULE_CODES.SETTINGS,
    MODULE_CODES.ACCOUNT_BILLING,
  ] as ModuleCode[],

  GROWTH: [
    MODULE_CODES.WEB_APP,
    MODULE_CODES.DASHBOARD,
    MODULE_CODES.SALES_MGMT,
    MODULE_CODES.INVENTORY,
    MODULE_CODES.CUSTOMER_MGMT,
    MODULE_CODES.REPORTS,
    MODULE_CODES.USER_MGMT,
    MODULE_CODES.SETTINGS,
    MODULE_CODES.ACCOUNT_BILLING,
    MODULE_CODES.PROCUREMENT,
    MODULE_CODES.SUPPLIER_MGMT,
    MODULE_CODES.MULTI_STORE,
    MODULE_CODES.LOYALTY_PROGRAM,
    MODULE_CODES.DISCOUNT,
    MODULE_CODES.DRUG_GENERICS,
    MODULE_CODES.COMPLIANCE,
  ] as ModuleCode[],

  PROFESSIONAL: [
    MODULE_CODES.WEB_APP,
    MODULE_CODES.DASHBOARD,
    MODULE_CODES.SALES_MGMT,
    MODULE_CODES.INVENTORY,
    MODULE_CODES.CUSTOMER_MGMT,
    MODULE_CODES.REPORTS,
    MODULE_CODES.USER_MGMT,
    MODULE_CODES.SETTINGS,
    MODULE_CODES.ACCOUNT_BILLING,
    MODULE_CODES.PROCUREMENT,
    MODULE_CODES.SUPPLIER_MGMT,
    MODULE_CODES.MULTI_STORE,
    MODULE_CODES.LOYALTY_PROGRAM,
    MODULE_CODES.DISCOUNT,
    MODULE_CODES.DOCTOR_MGMT,
    MODULE_CODES.HOSPITAL_MGMT,
    MODULE_CODES.DRUG_GENERICS,
    MODULE_CODES.COMPLIANCE,
    MODULE_CODES.DRUG_LICENSE,
    MODULE_CODES.DESKTOP_APP,
    MODULE_CODES.OFFLINE_MODE,
  ] as ModuleCode[],

  ENTERPRISE: ALL_MODULE_CODES,
} as const;

export type PlanType = keyof typeof PLAN_MODULES;
